import {
  extractFlexibleListRows,
  numberField,
  stringField,
  stringifyUnknown,
} from "./parseFields";
import type {
  ComplianceResult,
  ComplianceResultDetail,
  ComplianceResultFinding,
  ComplianceResultsList,
  ComplianceResultsPagination,
  ComplianceRunningImage,
  ComplianceTone,
  FindingComplianceFilter,
  FindingComplianceKind,
} from "./types";

const FINDING_USED_KEYS = new Set([
  "id",
  "uuid",
  "key",
  "name",
  "rule",
  "check",
  "title",
  "regulator",
  "status",
  "result",
  "outcome",
  "message",
  "details",
  "description",
  "error",
  "service",
  "runningImages",
  "running_images",
]);

function uniqueStrings(values: string[]): string[] {
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const value of values) {
    if (seen.has(value)) {
      continue;
    }
    seen.add(value);
    unique.push(value);
  }
  return unique;
}

function isImageDigest(value: string): boolean {
  return value.startsWith("sha256:") || /^[a-f0-9]{64}$/i.test(value);
}

function collectImageRefs(
  value: unknown,
  objectTagKeys: string[],
  objectHashKeys: string[],
): { tags: string[]; hashes: string[] } {
  const tags: string[] = [];
  const hashes: string[] = [];
  const items = Array.isArray(value)
    ? value
    : value == null || value === ""
      ? []
      : [value];
  for (const item of items) {
    if (typeof item === "string" && item.trim()) {
      const trimmed = item.trim();
      if (isImageDigest(trimmed)) {
        hashes.push(trimmed);
      } else {
        tags.push(trimmed);
      }
      continue;
    }
    if (item != null && typeof item === "object") {
      const record = item as Record<string, unknown>;
      const tag = stringField(record, ...objectTagKeys);
      const hash = stringField(record, ...objectHashKeys);
      if (tag) {
        tags.push(tag);
      }
      if (hash) {
        hashes.push(hash);
      }
    }
  }
  return { tags, hashes };
}

export function normalizeComplianceRunningImage(
  row: unknown,
): ComplianceRunningImage | null {
  if (row == null || typeof row !== "object") {
    return null;
  }
  const record = row as Record<string, unknown>;
  const repository = stringField(record, "repository");
  const componentName = stringField(record, "componentName", "component_name");
  const fromTags = collectImageRefs(
    record.tags ?? record.tag,
    ["tag", "name", "imageTag", "image_tag"],
    ["digest", "hash", "deployedHash", "deployed_hash", "imageId", "image_id"],
  );
  const fromIds = collectImageRefs(
    record.imageIds ?? record.image_ids,
    ["tag", "name"],
    ["digest", "hash", "imageId", "image_id"],
  );
  const fromHashes = collectImageRefs(
    record.deployedHashes ??
      record.deployed_hashes ??
      record.deployedHash ??
      record.deployed_hash,
    [],
    ["digest", "hash", "deployedHash", "deployed_hash"],
  );
  const explicitHash = stringField(
    record,
    "deployedHash",
    "deployed_hash",
    "digest",
    "imageDigest",
    "image_digest",
    "hash",
  );
  const tags = uniqueStrings([...fromTags.tags, ...fromIds.tags]);
  const deployedHashes = uniqueStrings([
    ...fromHashes.hashes,
    ...fromTags.hashes,
    ...fromIds.hashes,
    ...(explicitHash ? [explicitHash] : []),
  ]);
  const status = stringField(record, "status");
  const complianceCheck = stringField(
    record,
    "complianceCheck",
    "compliance_check",
  );
  const lastScannedAt = stringField(record, "lastScannedAt", "last_scanned_at");
  if (
    !repository &&
    !componentName &&
    tags.length === 0 &&
    deployedHashes.length === 0 &&
    !status &&
    !complianceCheck
  ) {
    return null;
  }
  return {
    repository,
    componentName,
    tags,
    deployedHashes,
    status,
    complianceCheck,
    lastScannedAt,
  };
}

function runningImagesField(
  record: Record<string, unknown>,
): ComplianceRunningImage[] {
  const raw = record.runningImages ?? record.running_images;
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .map(normalizeComplianceRunningImage)
    .filter((image): image is ComplianceRunningImage => image != null);
}

function emptyFinding(id: string, details: string): ComplianceResultFinding {
  return {
    id,
    name: "",
    status: "",
    details,
    service: "",
    runningImages: [],
  };
}

export function normalizeComplianceResultFinding(
  row: unknown,
  index: number,
): ComplianceResultFinding {
  if (row == null || typeof row !== "object") {
    return emptyFinding(String(index), stringifyUnknown(row));
  }
  const record = row as Record<string, unknown>;
  const rest: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(record)) {
    if (!FINDING_USED_KEYS.has(key) && value != null && value !== "") {
      rest[key] = value;
    }
  }
  const namedDetails = stringField(
    record,
    "message",
    "details",
    "description",
    "error",
  );
  const leftover = Object.keys(rest).length > 0 ? stringifyUnknown(rest) : "";
  const service = stringField(record, "service");
  const runningImages = runningImagesField(record);
  const status =
    stringField(record, "status", "result", "outcome") ||
    runningImages[0]?.status ||
    "";
  return {
    id: stringField(record, "id", "uuid", "key") || service || String(index),
    name: stringField(
      record,
      "name",
      "rule",
      "check",
      "title",
      "regulator",
      "service",
    ),
    status,
    details: [namedDetails, leftover].filter(Boolean).join(" "),
    service,
    runningImages,
  };
}

export function unwrapComplianceResultDetail(
  payload: unknown,
): Record<string, unknown> | null {
  if (payload == null || typeof payload !== "object") {
    return null;
  }
  const record = payload as Record<string, unknown>;
  const nested = record.data;
  if (
    nested != null &&
    typeof nested === "object" &&
    !Array.isArray(nested) &&
    (typeof (nested as Record<string, unknown>).id === "string" ||
      typeof (nested as Record<string, unknown>).status === "string")
  ) {
    return nested as Record<string, unknown>;
  }
  if (typeof record.id === "string" || typeof record.status === "string") {
    return record;
  }
  return null;
}

export function normalizeComplianceResultDetail(
  payload: unknown,
): ComplianceResultDetail | null {
  const record = unwrapComplianceResultDetail(payload);
  if (!record) {
    return null;
  }
  const id = stringField(record, "id");
  const status = stringField(record, "status", "status_summary", "statusSummary");
  if (!id && !status) {
    return null;
  }
  const nestedRows = Array.isArray(record.results)
    ? record.results
    : Array.isArray(record.findings)
      ? record.findings
      : extractFlexibleListRows(record.results);
  return {
    id,
    status,
    regulator: stringField(record, "regulator"),
    client: stringField(record, "client"),
    trigger: stringField(record, "trigger"),
    total: numberField(record, "total"),
    success: numberField(record, "success"),
    failed: numberField(record, "failed"),
    compliant: numberField(record, "compliant"),
    non_compliant: numberField(record, "non_compliant", "nonCompliant"),
    completed: numberField(record, "completed"),
    findings: nestedRows.map(normalizeComplianceResultFinding),
  };
}

export function normalizeComplianceResult(row: unknown): ComplianceResult | null {
  if (row == null || typeof row !== "object") {
    return null;
  }
  const record = row as Record<string, unknown>;
  const id = typeof record.id === "string" ? record.id.trim() : "";
  if (!id) {
    return null;
  }
  const status =
    typeof record.status_summary === "string"
      ? record.status_summary
      : typeof record.statusSummary === "string"
        ? record.statusSummary
        : typeof record.status === "string"
          ? record.status
          : "";
  const archivedAt =
    typeof record.archived_at === "string"
      ? record.archived_at
      : typeof record.archivedAt === "string"
        ? record.archivedAt
        : "";
  return {
    id,
    regulator: typeof record.regulator === "string" ? record.regulator : "",
    client: typeof record.client === "string" ? record.client : "",
    status_summary: status,
    archived_at: archivedAt,
    trigger: typeof record.trigger === "string" ? record.trigger : "",
  };
}

export function extractCursorMeta(
  response: unknown,
): ComplianceResultsPagination {
  if (response == null || typeof response !== "object") {
    return { nextCursor: null, previousCursor: null, returnedCount: 0 };
  }
  const pagination = (response as { pagination?: unknown }).pagination;
  if (pagination == null || typeof pagination !== "object") {
    return { nextCursor: null, previousCursor: null, returnedCount: 0 };
  }
  const page = pagination as Record<string, unknown>;
  const next = page.next_cursor ?? page.nextCursor;
  const previous = page.previous_cursor ?? page.previousCursor;
  const returned = page.returned_count ?? page.returnedCount;
  return {
    nextCursor: typeof next === "string" && next ? next : null,
    previousCursor: typeof previous === "string" && previous ? previous : null,
    returnedCount: typeof returned === "number" ? returned : 0,
  };
}

export function parseComplianceResultsList(
  payload: unknown,
): ComplianceResultsList {
  return {
    rows: extractFlexibleListRows(payload)
      .map(normalizeComplianceResult)
      .filter((row): row is ComplianceResult => row != null),
    pagination: extractCursorMeta(payload),
  };
}

function normalizeStatusKey(status: string): string {
  return status
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
}

export function complianceStatusTone(status: string): ComplianceTone {
  const key = normalizeStatusKey(status);
  if (["FAILED", "FAILURE", "FAIL", "ERROR"].includes(key)) {
    return "danger";
  }
  if (["NON_COMPLIANT"].includes(key)) {
    return "warning";
  }
  if (
    [
      "COMPLETED",
      "SUCCESS",
      "SUCCEEDED",
      "COMPLIANT",
      "PASSED",
      "PASS",
      "APPROVED",
    ].includes(
      key,
    )
  ) {
    return "success";
  }
  if (["RUNNING", "IN_PROGRESS", "PENDING"].includes(key)) {
    return "info";
  }
  return "neutral";
}

export function complianceCountTone(
  key:
    | "total"
    | "success"
    | "failed"
    | "compliant"
    | "non_compliant"
    | "completed",
): ComplianceTone {
  if (key === "success" || key === "compliant") {
    return "success";
  }
  if (key === "failed") {
    return "danger";
  }
  if (key === "non_compliant") {
    return "warning";
  }
  if (key === "completed") {
    return "info";
  }
  return "neutral";
}

export function findingComplianceKind(
  finding: ComplianceResultFinding,
): FindingComplianceKind {
  const tokens = [
    finding.status,
    ...finding.runningImages.flatMap((image) => [
      image.status,
      image.complianceCheck,
    ]),
  ]
    .map(normalizeStatusKey)
    .filter(Boolean);
  if (
    tokens.some((token) =>
      ["NON_COMPLIANT", "FAIL", "FAILED", "FAILURE", "ERROR"].includes(token),
    )
  ) {
    return "non_compliant";
  }
  if (
    tokens.some((token) =>
      ["COMPLIANT", "PASS", "PASSED", "SUCCESS", "SUCCEEDED"].includes(token),
    )
  ) {
    return "compliant";
  }
  return "unknown";
}

export function filterFindingsByServiceName(
  findings: ComplianceResultFinding[],
  query: string,
): ComplianceResultFinding[] {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return findings;
  }
  return findings.filter((finding) => {
    const haystack = [finding.service, finding.name]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(needle);
  });
}

export function filterFindingsByCompliance(
  findings: ComplianceResultFinding[],
  filter: FindingComplianceFilter,
): ComplianceResultFinding[] {
  if (filter === "all" || filter === "unknown") {
    return findings;
  }
  return findings.filter(
    (finding) => findingComplianceKind(finding) === filter,
  );
}

export function buildComplianceResultsListPath(
  basePath: string,
  cursor?: string,
): string {
  const trimmed = cursor?.trim();
  if (!trimmed) {
    return basePath;
  }
  return `${basePath}?${new URLSearchParams({ cursor: trimmed }).toString()}`;
}
